<script setup>
import {computed} from 'vue'
import {useProductStore} from "@js/store/ProductStore.js"
import ProductCharacteristicsFeature from "@js/components/Sections/Product/ProductCharacteristicsFeature.vue"
import {Link} from "@inertiajs/vue3"
import IconInfo from "@js/components/Icons/IconInfo.vue"

const productStore = useProductStore()

// helper для массивных свойств (collections, colors, surfaces и т.д.)
function generateRouteParam(type, id, catalog_type = 'tile') {
    const p = productStore.product
    if (!p && !id) {
        return null
    }

    return {
        category_title: p.category.slug,
        [type]: JSON.stringify([id]),
        catalog_type: catalog_type
    }
}

const productCharacteristics = computed(() => {
    const p = productStore.product
    if (!p) return []

    const list = [
        {
            title: 'Артикул',
            description: p.article
        },
        {
            title: 'Категория',
            description: p.category?.title,
            route_name: 'catalog',
            route_param: {category_title: p.category?.slug}
        },
        {
            title: 'Коллекция',
            description: p.collections?.length
                ? p.collections.map(c => ({
                    text: c.title,
                    route_name: 'showCollection',
                    route_param: {slug: c.slug}
                }))
                : null
        },
        {
            title: 'Производитель',
            description: p.brand?.title,
            route_name: 'catalog',
            route_param: generateRouteParam('brands', p.brand?.id)
        },
        {
            title: 'Страна',
            description: p.country?.title,
            route_name: 'catalog',
            route_param: generateRouteParam('countries', p.country?.id)
        },
        {title: 'Материал', description: p.material?.title},
        {
            title: 'Цвет',
            description: p.colors?.length
                ? p.colors.map(c => ({
                    text: c.title,
                    route_name: 'catalog',
                    route_param: generateRouteParam('colors', c.id)
                }))
                : null
        },
        {
            title: 'Поверхность',
            description: p.surfaces?.length
                ? p.surfaces.map(c => ({
                    text: c.title,
                    route_name: 'catalog',
                    route_param: generateRouteParam('surfaces', c.id)
                }))
                : null
        },
        {
            title: 'Помещения',
            description: p.rooms?.length
                ? p.rooms.map(c => ({
                    text: c.title,
                    route_name: 'catalog',
                    route_param: generateRouteParam('rooms', c.id)
                }))
                : null
        },
        {
            title: 'Рисунок',
            description: p.patterns?.length
                ? p.patterns.map(c => ({
                    text: c.title,
                    route_name: 'catalog',
                    route_param: generateRouteParam('patterns', c.id)
                }))
                : null
        },
        {title: 'Ректификат', description: p.is_rectification ? 'Да' : 'Нет'},
        {title: 'По акции', description: p.is_promotional ? 'Да' : 'Нет'},
        {
            title: 'Форма',
            description: p.forms?.length
                ? p.forms.map(c => ({
                    text: c.title,
                    route_name: 'catalog',
                    route_param: generateRouteParam('forms', c.id)
                }))
                : null
        },
        {title: 'Противоскольжение', description: p.anti_slip?.title},
        {title: 'Содержит разные рисунки', description: p.is_mixed_pattern ? 'Да' : 'Нет'},
        {
            title: 'Назначение',
            description: p.purposes?.length
                ? p.purposes.map(c => ({
                    text: c.title,
                    route_name: 'catalog',
                    route_param: generateRouteParam('purposes', c.id)
                }))
                : null
        },
        {
            title: 'Тип поверхности',
            description: p.terrains?.length
                ? p.terrains.map(c => ({
                    text: c.title,
                    route_name: 'catalog',
                    route_param: generateRouteParam('terrains', c.id)
                }))
                : null
        }
    ]

    return list.filter(item => item.description != null)
})

const packageCharacteristics = computed(() => {
    const p = productStore.product
    if (!p) return []

    const list = [
        {
            title: 'Размер',
            description: p.size?.title,
            route_name: 'catalog',
            route_param: generateRouteParam('sizes', p.size?.id)
        },
        {
            title: 'Площадь, м²',
            description: p.area != null ? `${p.area}` : null
        },
        {
            title: 'Толщина',
            description: p.depth != null ? `${p.depth}` : null
        },
        {
            title: 'Количество в коробке, шт.',
            description: p.count_in_box != null ? `${p.count_in_box}` : null
        },
        {
            title: 'Вес одной плитки, кг',
            description: p.one_piece_weight != null ? `${p.one_piece_weight}` : null
        },
        {
            title: 'Вес коробки, кг',
            description: p.box_weight != null ? `${p.box_weight}` : null
        },
        {
            title: 'Высота фишек, мм',
            description: p.chip_height != null ? `${p.chip_height}` : null
        },
        {
            title: 'Ширина фишек, мм',
            description: p.chip_width != null ? `${p.chip_width}` : null
        },
        {
            title: 'Износостойкость',
            description: p.resistance?.title,
            route_name: 'catalog',
            route_param: generateRouteParam('resistances', p.resistance?.id)
        },
        {
            title: 'Водопоглощение, %',
            description: p.water_absorption != null ? `${p.water_absorption}` : null
        },
        {
            title: 'Содержит разные рисунки',
            description: p.is_mixed_pattern ? 'Да' : null
        }
    ]

    return list.filter(item => item.description != null)
})
</script>

<template>
    <div class="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-10">
        <!-- Данные товара -->
        <div class="relative">
            <div class="px-4 sm:px-0 mb-2">
                <h3 class="text-base/7 font-semibold text-gray-900">Данные товара</h3>
            </div>
            <div class="border-t border-gray-200">
                <dl>
                    <div v-for="(item, key) in productCharacteristics" :key="key"
                         class="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 odd:bg-white even:bg-gray-100 md:hover:bg-gray-200 transition">
                        <dt class="text-sm/6 font-medium text-gray-900">{{ item.title }}</dt>
                        <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 md:text-right">
                            <!-- Массивные описания -->
                            <template v-if="Array.isArray(item.description)">
                                <span v-for="(desc, idx) in item.description" :key="idx">
                                    <template v-if="desc.route_param">
                                        <Link class="hover:text-navHoverColor transition underline underline-offset-4"
                                              :href="route(desc.route_name, desc.route_param)">
                                            {{ desc.text }}
                                        </Link>
                                    </template>
                                    <template v-else>
                                        {{ desc.text }}
                                    </template>
                                    <span v-if="idx < item.description.length - 1">, </span>
                                </span>
                            </template>
                            <!-- Примитивные описания -->
                            <template v-else>
                                <template v-if="item.route_param">
                                    <Link :href="route(item.route_name, item.route_param)"
                                          class="hover:text-navHoverColor transition underline underline-offset-4">
                                        {{ item.description }}
                                    </Link>
                                </template>
                                <template v-else>
                                    {{ item.description }}
                                </template>
                            </template>
                        </dd>
                    </div>
                </dl>
            </div>
        </div>
        <div class="relative">
            <div class="px-4 sm:px-0 mb-2">
                <h3 class="text-base/7 font-semibold text-gray-900">Параметры товара</h3>
            </div>
            <div class="border-t border-gray-200">
                <dl>
                    <div v-for="(item, key) in packageCharacteristics" :key="key"
                        class="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 odd:bg-white even:bg-gray-100 md:hover:bg-gray-200 transition">
                        <dt class="text-sm/6 font-medium text-gray-900">{{ item.title }}</dt>
                        <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 md:text-right">
                            <template v-if="item.route_param">
                                <Link :href="route(item.route_name, {
                                             ...item.route_param,
                                            })"
                                    class="hover:text-navHoverColor transition underline underline-offset-4">
                                    {{ item.description }}
                                </Link>
                            </template>
                            <template v-else>
                                {{ item.description }}
                            </template>
                        </dd>
                    </div>
                </dl>
            </div>
        </div>
        <div>
            <div class="flex flex-col gap-2 text-xs leading-4">
                <p class="flex items-center gap-2 text-sm font-medium">
                    <IconInfo class="w-5 h-5"/>
                    Тон и калибр плитки может отличаться
                </p>
                <p>
                    Тон плитки — цветовой нюанс определенной партии продукции. Расцветки и эстетические характеристики
                    материалов керамической плитки на экране монитора может отличаться от реального.
                </p>
                <p>
                    Указанные на сайте размеры (калибр плитки) – номинальные. Фактический размер плитки будет
                    отклоняться от
                    номинального в пределах определенного калибра, как в меньшую, так и в большую сторону.
                </p>
                <p>
                    Информация о тоне (цвете) и калибре (производственный размер) указана на упаковке товара.
                </p>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
</style>
